// src/utils/Api.js
import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: Number(process.env.REACT_APP_API_TIMEOUT) || 10000,
  // withCredentials: true // habilitar si usas cookies de sesión
});

const handleUnauthorized = () => {
  try {
    ['token', 'rol', 'correo', 'casilleroCodigo'].forEach((key) => localStorage.removeItem(key));
  } catch (err) {
    console.error('[API] ❌ Error limpiando localStorage:', err.message);
  }
  window.location.href = '/login';
};

const notifyError = (message) => {
  console.error('[API] ❌', message);
  // Aquí podrías integrar un toast global si lo deseas
};

const getCasilleroCodigo = () => localStorage.getItem('casilleroCodigo') || null;

// ✅ Interceptor de request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;

    const casillero = getCasilleroCodigo();
    if (casillero) config.headers['X-Casillero-Codigo'] = casillero;

    return config;
  },
  (error) => Promise.reject(error)
);

// ✅ Interceptor de respuesta
api.interceptors.response.use(
  (response) => {
    const url = response.config?.url || '';
    const data = response.data;

    // Aceptar 200, 201 y 204 como éxito
    if (![200, 201, 204].includes(response.status)) {
      return Promise.reject({ response: { status: response.status, data } });
    }

    // Si el backend devuelve success: false → error
    if (data && data.success === false) {
      return Promise.reject({ response: { status: response.status, data } });
    }

    // Guardar datos de sesión en login/register/validate
    if (url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/validate')) {
      const { token, usuario, casillero } = data || {};
      try {
        if (token) localStorage.setItem('token', token);
        if (usuario?.rol) localStorage.setItem('rol', usuario.rol);
        if (usuario?.correo) localStorage.setItem('correo', usuario.correo);
        if (casillero?.codigo) localStorage.setItem('casilleroCodigo', casillero.codigo);
      } catch (err) {
        console.error('[API] ❌ Error guardando sesión en localStorage:', err.message);
      }
    }

    // Si no hay body (204) → devolver response completo
    if (typeof data === 'undefined' || response.status === 204) {
      return response;
    }

    return data;
  },
  (error) => {
    if (error.code === 'ECONNABORTED') {
      notifyError('⏱️ Tiempo de espera agotado en la petición');
    } else if (error.response) {
      const { status, data, statusText } = error.response;
      switch (status) {
        case 401:
          notifyError(data?.mensaje || 'Token inválido o expirado');
          handleUnauthorized();
          break;
        case 403:
          notifyError(data?.mensaje || 'Acceso denegado: no tienes permisos suficientes');
          break;
        default:
          notifyError(data?.mensaje || `Error ${status} ${statusText || ''}`.trim());
          break;
      }
      return Promise.reject({ response: { status, data } });
    } else {
      notifyError('🌐 Error de red o servidor no disponible');
      return Promise.reject({ response: { status: 0, data: null } });
    }
  }
);

export { handleUnauthorized, notifyError, getCasilleroCodigo };
export default api;